<template>
  <b-modal
    :active.sync="isModalActive"
    has-modal-card
    class="is-medium is-wider-modal"
  >
    <div class="modal-card">
      <header class="modal-card-head">
        <h1 class="is-title-header modal-card-title">
          {{ action }} Data Manipulation
          <span v-if="hasUnsavedChanges" class="unsaved-changes-indicator">
            (Please Save Column Changes)
          </span>
        </h1>
        <b-button
          type="is-primary"
          size="is-small"
          @click="showGuide"
          class="guide-button"
        >
          <i class="mdi mdi-information-outline"></i>
          Data Manipulation Guide
        </b-button>
      </header>
      <section class="modal-card-body enable-select-text">
        <!-- Table header -->
        <div class="data-table-header">
          <div class="column-name-header">Column Name</div>
          <div class="source-header">Source</div>
          <div class="action-header"></div>
        </div>

        <!-- Existing columns -->
        <div
          v-for="column in filteredColumns"
          :key="column.field"
          class="data-table-row"
        >
          <div class="column-name-cell">
            <template v-if="editingField === column.field">
              <input v-model="editBuffer.columnLabel" class="input" />
            </template>
            <template v-else>
              <div class="field-label">{{ column.label }}</div>
            </template>
          </div>

          <div class="source-cell">
            <template v-if="editingField === column.field">
              <textarea
                v-if="isLongText(editBuffer[column.field])"
                v-model="editBuffer[column.field]"
                class="textarea"
                style="width: 100%; min-height: 50px;"
              ></textarea>
              <input
                v-else
                v-model="editBuffer[column.field]"
                class="input"
                style="width: 100%;"
              />
            </template>
            <template v-else>
              <div class="formula-content">
                {{ column.field_formula }}
                <span v-if="column.field_error" class="additional-info">
                  <a
                    href="#"
                    class="is-danger"
                    @click.prevent="additionalInfo(column.field_error)"
                  >
                    <i class="mdi mdi-information is-danger"></i>
                  </a>
                </span>
              </div>
            </template>
          </div>

          <div class="action-cell">
            <template v-if="editingField === column.field">
              <a href="#" @click.prevent="saveField(column.field)">
                <i class="mdi mdi-check mdi-24px is-success"></i>
              </a>
            </template>
            <template v-else>
              <div class="action-buttons-container is-primary">
                <a
                  href="#"
                  v-if="column.is_editable"
                  @click.prevent="editField(column.field)"
                >
                  <i
                    class="mdi mdi-pencil mdi-24px"
                    style="color: #2BABE2;"
                  ></i>
                </a>
                <a
                  href="#"
                  v-if="column.is_editable"
                  @click.prevent="deleteField(column.field)"
                >
                  <i class="mdi mdi-delete mdi-24px is-danger"></i>
                </a>
                <a href="#" v-else>
                  <i class="mdi mdi-pencil mdi-24px disabled"></i>
                </a>
              </div>
            </template>
          </div>
        </div>

        <!-- New columns -->
        <div
          v-for="(col, idx) in newColumns"
          :key="'newcol-' + idx"
          class="data-table-row"
        >
          <div class="column-name-cell">
            <input
              v-model="col.label"
              class="input"
              placeholder="Column Label"
            />
          </div>

          <div class="source-cell">
            <textarea
              v-if="isLongText(col.field_formula)"
              v-model="col.field_formula"
              class="textarea"
              placeholder="Source"
              style="width: 100%; min-height: 50px;"
            ></textarea>
            <input
              v-else
              v-model="col.field_formula"
              class="input"
              placeholder="Source"
              style="width: 100%;"
              @input="checkTextLength(idx)"
            />
          </div>

          <div class="action-cell">
            <div class="action-buttons-container">
              <a href="#" @click.prevent="saveSingleNewColumn(idx)">
                <i class="mdi mdi-check mdi-24px is-success" title="Save"></i>
              </a>
              <a href="#" @click.prevent="removeNewColumn(idx)">
                <i class="mdi mdi-close mdi-24px is-danger" title="Remove"></i>
              </a>
            </div>
          </div>
        </div>
      </section>
      <footer class="modal-card-foot">
        <b-button
          slot="right"
          type="is-normal"
          @click="addColumn"
          class="add-column-btn"
        >
          <i class="mdi mdi-plus"></i> Add Column
        </b-button>
        <b-button
          slot="right"
          type="is-primary"
          @click="saveData"
          :disabled="hasUnsavedChanges"
          title="Save all changes"
        >
          Save
        </b-button>
        <b-button slot="right" type="is-danger" @click="cancel">
          Cancel
        </b-button>
      </footer>
    </div>
    <data-manipulation-guide
      :is-active="isGuideModalActive"
      @close="isGuideModalActive = false"
    />
  </b-modal>
</template>
<script>
import DataManipulationGuide from "./DataManipulationGuide.vue";

export default {
  name: "SingleProductEdit",
  components: {
    DataManipulationGuide
  },
  props: {
    action: {
      type: String,
      default: ""
    },
    isAdd: {
      type: Boolean,
      default: true
    },
    isActive: {
      type: Boolean,
      default: false
    },
    columns: {
      type: Array
    }
  },
  data() {
    return {
      isModalActive: false,
      selectDisable: true,
      editingField: null,
      editBuffer: {},
      addingColumn: false,
      newColumnLabel: "",
      newColumnValue: "",
      newColumns: [],
      isGuideModalActive: false
    };
  },
  computed: {
    // Check if there are any unsaved changes that should block the save button
    hasUnsavedChanges() {
      // If any column is currently being edited
      if (this.editingField !== null) {
        return true;
      }

      // If there are any new columns that haven't been saved yet with saveSingleNewColumn
      if (this.newColumns.length > 0) {
        return true;
      }

      return false;
    },

    // Filter columns to show only relevant ones
    filteredColumns() {
      return this.columns.filter(function(c) {
        return (
          !c.is_deleted &&
          c.field !== "actions" &&
          c.field !== "created_at" &&
          c.field !== "Updated Date" &&
          c.field !== "created_by_name" &&
          c.field !== "Updated By" &&
          c.field !== "data_validation"
        );
      });
    }
  },
  methods: {
    showGuide() {
      this.isGuideModalActive = true;
    },
    isLongText(text) {
      // Check if text is longer than 50 characters or contains line breaks
      return text && (text.length > 50 || text.includes("\n"));
    },
    // Update the editField method to detect initial text length and store column label
    editField(field) {
      this.editingField = field;
      const col = this.columns.find(c => c.field === field);

      if (col) {
        this.editBuffer[field] = col.field_formula || "";
        this.editBuffer.columnLabel = col.label || ""; // Store the column label for editing

        // If text is long, let's automatically convert it to textarea format
        if (this.isLongText(this.editBuffer[field])) {
          // If there are no line breaks but text is long, add some for readability
          if (
            !this.editBuffer[field].includes("\n") &&
            this.editBuffer[field].length > 100
          ) {
            // Add line breaks for readability (roughly every 80 chars)
            this.editBuffer[field] = this.editBuffer[field].replace(
              /(.{80})/g,
              "$1\n"
            );
          }
        }
      }
    },
    checkTextLength(idx) {
      const col = this.newColumns[idx];
      // Force Vue to re-render by making a small change to trigger the v-if
      if (this.isLongText(col.field_formula)) {
        const formula = col.field_formula;
        col.field_formula = "";
        this.$nextTick(() => {
          col.field_formula = formula;
        });
      }
    },
    additionalInfo(fieldError) {
      this.$buefy.dialog.alert({
        title: "Additional Information",
        message: fieldError,
        confirmText: "OK"
      });
    },
    addColumn() {
      this.newColumns.push({ label: "", field_formula: "", is_deleted: false });
    },
    removeNewColumn(idx) {
      this.$buefy.dialog.confirm({
        title: "Confirm Delete",
        message: "Are you sure you want to delete this column?",
        confirmText: "Delete",
        cancelText: "Cancel",
        type: "is-danger",
        hasIcon: true,
        onConfirm: () => {
          this.newColumns.splice(idx, 1);
        }
      });
    },
    saveSingleNewColumn(idx) {
      const col = this.newColumns[idx];
      if (!col.label) {
        this.$buefy.toast.open({
          message: "Column label cannot be empty.",
          type: "is-danger"
        });
        return;
      }

      // Check for duplicate column names
      const isDuplicate = this.columns.some(
        existingCol =>
          existingCol.label.toLowerCase() === col.label.toLowerCase() &&
          !existingCol.is_deleted
      );

      // Also check other unsaved new columns
      const isDuplicateInNew = this.newColumns.some(
        (newCol, i) =>
          i !== idx && newCol.label.toLowerCase() === col.label.toLowerCase()
      );

      if (isDuplicate || isDuplicateInNew) {
        this.$buefy.toast.open({
          message:
            "A column with this name already exists. Please choose a different name.",
          type: "is-danger",
          duration: 5000
        });
        return;
      }

      // If not a duplicate, add the column
      this.columns.push({
        field: col.label,
        field_formula: col.field_formula,
        is_editable: true,
        label: col.label,
        is_deleted: false
      });
      this.newColumns.splice(idx, 1);
    },
    // editField(field) {
    //   this.editingField = field;
    //   const col = this.columns.find(c => c.field === field);
    //   this.editBuffer[field] = col ? col.field_formula : "";
    // },
    deleteField(field) {
      // Show confirmation dialog before deleting
      this.$buefy.dialog.confirm({
        title: "Confirm Delete",
        message: "Are you sure you want to delete this column?",
        confirmText: "Delete",
        cancelText: "Cancel",
        type: "is-danger",
        hasIcon: true,
        onConfirm: () => {
          // Only delete after confirmation
          const col = this.columns.find(c => c.field === field);
          if (col) {
            // Use Vue.set to ensure reactivity
            this.$set(col, "field_formula", "");
            this.$set(col, "is_editable", true);
            this.$set(col, "is_deleted", true); // Mark as deleted

            // Mark that changes need to be saved
            this.hasUnsavedChanges = true;
          }
          this.editingField = null;

          this.$buefy.toast.open({
            message: "Column marked for deletion. Save changes to apply.",
            type: "is-warning"
          });
        }
      });
    },
    saveField(field) {
      const col = this.columns.find(c => c.field === field);
      if (col) {
        // Check if column name is being changed
        const newColumnLabel = this.editBuffer.columnLabel?.trim();

        if (!newColumnLabel) {
          this.$buefy.toast.open({
            message: "Column name cannot be empty.",
            type: "is-danger"
          });
          return;
        }

        // Check for duplicate names if the name is changed
        if (newColumnLabel !== col.label) {
          const isDuplicate = this.columns.some(
            existingCol =>
              existingCol !== col &&
              existingCol.label.toLowerCase() ===
                newColumnLabel.toLowerCase() &&
              !existingCol.is_deleted
          );

          if (isDuplicate) {
            this.$buefy.toast.open({
              message:
                "A column with this name already exists. Please choose a different name.",
              type: "is-danger",
              duration: 5000
            });
            return;
          }

          // Update the column name and field
          col.label = newColumnLabel;
          col.field = newColumnLabel;
        }

        // Update formula
        col.field_formula = this.editBuffer[field];
        col.is_editable = true;
        col.is_deleted = false; // Mark as not deleted
      }
      this.editingField = null;
    },
    cancel() {
      // If there are unsaved changes, ask for confirmation
      if (this.hasUnsavedChanges) {
        this.$buefy.dialog.confirm({
          title: "Unsaved Column Changes",
          message:
            "You have columns being edited or new columns that haven't been saved. Are you sure you want to discard them?",
          confirmText: "Discard Changes",
          cancelText: "Continue Editing",
          type: "is-danger",
          hasIcon: true,
          onConfirm: () => {
            this.editingField = null;
            this.editBuffer = {};
            this.newColumns = [];
            this.$emit("cancel");
          }
        });
      } else {
        this.$emit("cancel");
      }
    },
    saveData() {
      let hasError = false;
      const validNewColumns = [];

      // Validate all new columns before adding them
      this.newColumns.forEach(col => {
        if (!col.label) return;

        // Check for duplicate column names
        const isDuplicate = [...this.columns, ...validNewColumns].some(
          existingCol =>
            existingCol.label.toLowerCase() === col.label.toLowerCase() &&
            !existingCol.is_deleted
        );

        if (isDuplicate) {
          hasError = true;
          this.$buefy.toast.open({
            message: `Column "${col.label}" already exists. Skipping this column.`,
            type: "is-warning",
            duration: 5000
          });
        } else {
          validNewColumns.push({
            field: col.label,
            field_formula: col.field_formula,
            is_editable: true,
            label: col.label,
            is_deleted: col.is_deleted || false
          });
        }
      });

      // Add validated new columns
      this.columns.push(...validNewColumns);
      this.newColumns = [];

      if (hasError) {
        this.$buefy.toast.open({
          message: "Some columns were skipped due to duplicate names.",
          type: "is-warning",
          duration: 5000
        });
      }

      this.$emit("updateColumns", this.columns);
    }
  },
  watch: {
    isActive(newValue) {
      this.isModalActive = newValue;
    },
    isModalActive(newValue) {
      if (!newValue) {
        this.$emit("cancel");
      }
    }
  }
};
</script>
<style scoped>
.is-wider-modal .modal-card {
  max-width: 750px !important;
  width: 100%;
}

.unsaved-changes-indicator {
  font-size: 0.8rem;
  color: #f14668;
  margin-left: 10px;
  font-weight: normal;
  font-style: italic;
}

.help-text {
  font-size: 0.85rem;
  color: #3273dc;
  margin-left: 10px;
  display: flex;
  align-items: center;
}

.help-text i {
  margin-right: 5px;
}

.modal-card-foot {
  flex-wrap: wrap;
}

/* Add consistent styling for rows */
.field-row {
  margin-bottom: 8px !important;
  padding: 5px 0;
  border-bottom: 1px solid #f0f0f0;
  position: relative;
  width: 100%;
}

/* Grid table styling */
.data-table-header {
  display: flex;
  width: 100%;
  padding: 10px 0;
  border-bottom: 2px solid #dbdbdb;
  font-weight: bold;
  margin-bottom: 5px;
  position: sticky;
  top: 0;
  background-color: white;
  z-index: 10;
}

.data-table-row {
  display: flex;
  width: 100%;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  align-items: center;
}

.data-table-row:hover {
  background-color: #f9f9f9;
}

.column-name-header {
  flex: 0 0 220px;
  padding-right: 10px;
}

.source-header {
  flex: 1;
}

.action-header {
  flex: 0 0 60px;
  text-align: right;
}

.column-name-cell {
  flex: 0 0 220px;
  padding-right: 10px;
  overflow: hidden;
}

.source-cell {
  flex: 1;
  padding-right: 10px;
}

.action-cell {
  flex: 0 0 60px;
  text-align: right;
}

.action-buttons-container {
  display: flex;
  gap: 4px;
  justify-content: flex-end;
}

.field-label {
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  line-height: 1.2;
  padding: 4px 0;
  text-align: left !important;
}

.formula-content {
  word-break: break-word;
  overflow-wrap: break-word;
  padding: 4px 0;
  display: flex;
  align-items: center;
}

/* Action link styling */
a {
  color: inherit;
  text-decoration: none;
}

/* For inputs and textareas */
input.input,
textarea.textarea {
  width: 100%;
  max-width: 100%;
}

/* Add hover effect for better UX */
.field-row:hover {
  background-color: #f9f9f9;
}

/* Optimize modal body layout */
.modal-card-body {
  padding: 15px 25px 15px 15px;
  max-height: 60vh;
  overflow-y: auto !important;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
}

/* Make sure the modal is large enough */
.is-wider-modal .modal-card {
  width: 80vw;
  max-width: 1200px;
}

/* Ensure Add Column button is always visible */
.modal-card-foot {
  position: sticky;
  bottom: 0;
  background-color: white;
  z-index: 10;
}

/* Style the Add Column button */
.add-column-btn {
  font-weight: bold;
}

/* Guide button styling */
.guide-button {
  margin-left: auto;
  white-space: nowrap;
}

.modal-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.modal-card-title {
  flex: 1;
}
</style>
